import {Yup} from 'utils/validators';
import {StringSchema} from 'yup';

import dayjs from 'utils/dayjs';
import {displayDateFormat, displayTimeMeridiem} from 'utils/date-time';

import {
    BETA_COMP_PASS_LIMIT,
    TRANSIENT_LABEL_MAX_LENGTH,
    TRANSIENT_MAX_DURATION_HOURS,
    calculateTotalNumberOfPasses,
} from 'pages/comp-pass/components/comp-pass-wizard/utils';

const phoneRegExp =
    /^(\+?1[-.\s]?)?\(?([2-9][0-9][0-9])\)?[-.\s]?([0-9][0-9]{2})[-.\s]?([0-9]{4})$/;

export const ERROR_MESSAGES_RECIPIENT = {
    email: {
        invalid: 'Invalid email.',
        required: 'This field is required.',
    },
    phoneNumber: {
        invalid: 'Invalid phone number.',
    },
    licensePlate: {
        invalid: 'Invalid license plate format.',
        notEnoughCharacters: 'Minimum of 1 character.',
        tooManyCharacters: 'Maximum of 8 characters.',
    },
    quantity: {
        notEnoughPasses: 'Minimum of 1 pass per event.',
        notEnoughPassesTransient: 'Minimum of 1 pass per timeframe.',
        required: 'This field is required.',
        integersOnly: 'Must be a whole number.',
        maxQuantityExceeded: `Comp Pass limit exceeded. You can create up to ${BETA_COMP_PASS_LIMIT} passes at a time.`,
    },
};

/**
 * Yup schema factory for validating the comp pass recipient form. Pass
 * `isTransientFlow` so quantity copy reads "timeframe" instead of "event".
 */
export const getCompPassRecipientValidationSchema = (isTransientFlow = false) =>
    Yup.array()
        .of(
            Yup.object().shape(
                {
                    email: Yup.string()
                        .label('Email')
                        .email(ERROR_MESSAGES_RECIPIENT.email.invalid)
                        .required(ERROR_MESSAGES_RECIPIENT.email.required),
                    ccEmail: Yup.string()
                        .label('CC Email')
                        .when(
                            'ccEmail',
                            (
                                val: string,
                                schema: StringSchema<string | undefined>
                            ) => {
                                if (val?.length > 0) {
                                    return schema.email(
                                        ERROR_MESSAGES_RECIPIENT.email.invalid
                                    );
                                } else {
                                    return Yup.string().notRequired();
                                }
                            }
                        ),
                    phoneNumber: Yup.string()
                        .label('Phone Number')
                        .when(
                            'phoneNumber',
                            (
                                val: string,
                                schema: StringSchema<string | undefined>
                            ) => {
                                if (val?.length > 0) {
                                    return schema.matches(
                                        phoneRegExp,
                                        ERROR_MESSAGES_RECIPIENT.phoneNumber
                                            .invalid
                                    );
                                } else {
                                    return Yup.string().notRequired();
                                }
                            }
                        ),
                    licensePlate: Yup.string()
                        .label('License Plate')
                        .when(
                            'licensePlate',
                            (
                                val: string,
                                schema: StringSchema<string | undefined>
                            ) => {
                                if (val?.length > 0) {
                                    return schema
                                        .min(
                                            1,
                                            ERROR_MESSAGES_RECIPIENT
                                                .licensePlate
                                                .notEnoughCharacters
                                        )
                                        .max(
                                            8,
                                            ERROR_MESSAGES_RECIPIENT
                                                .licensePlate.tooManyCharacters
                                        );
                                } else {
                                    return Yup.string().notRequired();
                                }
                            }
                        ),
                    quantity: Yup.number()
                        .label(
                            isTransientFlow
                                ? 'Passes Per Timeframe'
                                : 'Passes Per Event'
                        )
                        .transform((value) => {
                            // eslint-disable-next-line no-undefined
                            return isNaN(value) ? undefined : value;
                        })
                        .required(ERROR_MESSAGES_RECIPIENT.quantity.required)
                        .min(
                            1,
                            isTransientFlow
                                ? ERROR_MESSAGES_RECIPIENT.quantity
                                      .notEnoughPassesTransient
                                : ERROR_MESSAGES_RECIPIENT.quantity
                                      .notEnoughPasses
                        )
                        .integer(
                            ERROR_MESSAGES_RECIPIENT.quantity.integersOnly
                        ),
                },
                [
                    ['email', 'email'],
                    ['ccEmail', 'ccEmail'],
                    ['phoneNumber', 'phoneNumber'],
                    ['licensePlate', 'licensePlate'],
                ]
            )
        )
        .test({
            name: 'max-comp-passes',
            message: ERROR_MESSAGES_RECIPIENT.quantity.maxQuantityExceeded,
            params: {limit: BETA_COMP_PASS_LIMIT},
            test: (value, context) => {
                const selectionCount = context.options.context.selectionCount;
                const totalNumberOfPasses = calculateTotalNumberOfPasses({
                    recipients: value,
                    selectionCount,
                });

                const asNumber = isNaN(totalNumberOfPasses)
                    ? 0
                    : totalNumberOfPasses;

                return asNumber <= BETA_COMP_PASS_LIMIT;
            },
        });

/**
 * Default recipient validation schema (event-flow copy). Prefer
 * `getCompPassRecipientValidationSchema(isTransientFlow)` when the flow is known.
 */
export const compPassRecipientValidationSchema =
    getCompPassRecipientValidationSchema();

export const ERROR_MESSAGES_TRANSIENT = {
    required: 'This field is required.',
    labelTooLong: `${TRANSIENT_LABEL_MAX_LENGTH} character limit.`,
    durationTooLong: `Can't exceed ${TRANSIENT_MAX_DURATION_HOURS} hours.`,
    startMustBeInFuture: 'Start time must be in the future.',
    endBeforeStart: 'End time must be after the start time.',
};

const TRANSIENT_DATE_TIME_FORMATS = [
    `${displayDateFormat} ${displayTimeMeridiem}`,
    `${displayDateFormat} ${displayTimeMeridiem.toLowerCase()}`,
];

const parseDateTime = (date: string, time: string): dayjs.Dayjs | null => {
    if (!date || !time) {
        return null;
    }

    const parsed = dayjs(`${date} ${time}`, TRANSIENT_DATE_TIME_FORMATS, true);

    return parsed.isValid() ? parsed : null;
};

interface TransientRowShape {
    id: string;
    label: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
}

/**
 * Yup schema for validating the transient time-frame draft rows. Three
 * row-level tests attach cross-field errors: `start-must-be-in-future` to
 * `startTime`, and `duration-within-24-hours` and `end-must-be-after-start`
 * to `endTime`.
 */
export const transientTimeFrameValidationSchema =
    Yup.array<TransientRowShape>().of(
        Yup.object()
            .shape({
                id: Yup.string().required(),
                label: Yup.string()
                    .label('Label')
                    .required(ERROR_MESSAGES_TRANSIENT.required)
                    .max(
                        TRANSIENT_LABEL_MAX_LENGTH,
                        ERROR_MESSAGES_TRANSIENT.labelTooLong
                    ),
                startDate: Yup.string()
                    .label('Start Date')
                    .required(ERROR_MESSAGES_TRANSIENT.required),
                startTime: Yup.string()
                    .label('Start Time')
                    .required(ERROR_MESSAGES_TRANSIENT.required),
                endDate: Yup.string()
                    .label('End Date')
                    .required(ERROR_MESSAGES_TRANSIENT.required),
                endTime: Yup.string()
                    .label('End Time')
                    .required(ERROR_MESSAGES_TRANSIENT.required),
            })
            .test({
                name: 'start-must-be-in-future',
                test(row) {
                    const start = parseDateTime(row.startDate, row.startTime);

                    if (!start || !start.isBefore(dayjs())) {
                        return true;
                    }

                    return this.createError({
                        path: `${this.path}.startTime`,
                        message: ERROR_MESSAGES_TRANSIENT.startMustBeInFuture,
                    });
                },
            })
            .test({
                name: 'duration-within-24-hours',
                test(row) {
                    const start = parseDateTime(row.startDate, row.startTime);
                    const end = parseDateTime(row.endDate, row.endTime);

                    if (!start || !end) {
                        return true;
                    }

                    const max = start.add(TRANSIENT_MAX_DURATION_HOURS, 'hour');

                    if (!end.isAfter(max)) {
                        return true;
                    }

                    return this.createError({
                        path: `${this.path}.endTime`,
                        message: ERROR_MESSAGES_TRANSIENT.durationTooLong,
                    });
                },
            })
            .test({
                name: 'end-must-be-after-start',
                test(row) {
                    const start = parseDateTime(row.startDate, row.startTime);
                    const end = parseDateTime(row.endDate, row.endTime);

                    if (!start || !end || end.isAfter(start)) {
                        return true;
                    }

                    return this.createError({
                        path: `${this.path}.endTime`,
                        message: ERROR_MESSAGES_TRANSIENT.endBeforeStart,
                    });
                },
            })
    );
